'use client'

import { clsx } from 'clsx'
import {
  type ComponentPropsWithoutRef,
  type ElementType,
  type FC,
  useRef,
} from 'react'

import { type CrossSellProduct } from '@redteclab/api/clients/retail-media-xsell-service'

import { AD_SERVER_XSELL_PRODUCT_ID_DATA_ATTR } from '../../../ad-server'
import {
  CrossSellProductImage,
  CrossSellProductName,
  CrossSellProductQuantity,
} from '../../../cross-sell'

import { CartIntermediateSpecialOffersBuyProductButton } from './CartIntermediateSpecialOffersBuyProductButton'
import { CartIntermediateSpecialOffersImageDecorator } from './CartIntermediateSpecialOffersImageDecorator'
import { CartIntermediateSpecialOffersProductDiscountWithPrice } from './CartIntermediateSpecialOffersProductDiscountWithPrice'

type CartIntermediateSpecialOffersCrossSellProductCardProps =
  ComponentPropsWithoutRef<'section'> & {
    product?: CrossSellProduct
    slots?: {
      buyButton?: ElementType
      image?: ElementType
    }
  }

export const CartIntermediateSpecialOffersCrossSellProductCard: FC<
  CartIntermediateSpecialOffersCrossSellProductCardProps
> = ({ className, product: productProp, slots, ...rest }) => {
  const product = productProp
  const ImageComponent = slots?.image ?? CrossSellProductImage
  const ref = useRef<HTMLElement>(null)

  const sectionProps = {
    /*
     * Re-ranked / injected elements from ad server needs to be marked here
     * They're being grabbed in ProductAvailableCrossSellSliderWidget.tsx
     * and pushed into ad server data layer
     */
    [AD_SERVER_XSELL_PRODUCT_ID_DATA_ATTR]: product?.id ?? undefined,
    className: clsx(
      'flex flex-1 justify-center overflow-hidden rounded-2xl bg-light-primary-low pl-4 shadow-m',
      className,
    ),
    'data-product-id': product?.id,
    ref,
    ...rest,
  }

  return (
    <section {...sectionProps}>
      <div className="relative flex flex-1 items-center">
        <ImageComponent
          product={product}
          slots={{ decorator: CartIntermediateSpecialOffersImageDecorator }}
        />
        <div className="flex flex-1 flex-col gap-1 px-2 py-2.5">
          <CrossSellProductName
            className="line-clamp-2 min-h-0"
            product={product}
          />
          <CrossSellProductQuantity
            className="line-clamp-1 min-h-0"
            product={product}
          />
          <CartIntermediateSpecialOffersProductDiscountWithPrice
            product={product}
          />
        </div>
        <CartIntermediateSpecialOffersBuyProductButton
          className="mb-4 mr-4 self-end"
          product={product}
        />
      </div>
    </section>
  )
}
